/**
 * taskflow-audit-log.ts
 *
 * Append-Only Activity Audit Ledger for TaskFlow.
 * Computes structured field diffs and maintains bounded in-memory/persisted
 * history per item to prevent storage bloat.
 */

import type { TaskFlowItem } from '../contracts/taskflow-schema.js';

export interface FieldDiff {
  readonly field: string;
  readonly oldValue: unknown;
  readonly newValue: unknown;
}

export interface TaskFlowAuditEntry {
  readonly entryId: string;
  readonly itemId: string;
  readonly actorId: string;
  readonly timestamp: number;
  readonly action: 'created' | 'updated' | 'deleted' | 'archived' | 'restored';
  readonly diffs: FieldDiff[];
  readonly snapshotSummary?: string;
}

export interface AuditLoggerOptions {
  readonly maxEntriesPerItem?: number;
}

export class TaskFlowAuditLogger {
  private readonly history = new Map<string, TaskFlowAuditEntry[]>();
  private readonly maxEntriesPerItem: number;

  constructor(options: AuditLoggerOptions = {}) {
    this.maxEntriesPerItem = options.maxEntriesPerItem ?? 50;
  }

  /**
   * Record task creation
   */
  recordCreation(item: TaskFlowItem, actorId: string): TaskFlowAuditEntry {
    const entry: TaskFlowAuditEntry = {
      entryId: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      itemId: item.id,
      actorId,
      timestamp: Date.now(),
      action: 'created',
      diffs: [
        { field: 'type', oldValue: null, newValue: item.type },
        { field: 'title', oldValue: null, newValue: item.title },
        { field: 'status', oldValue: null, newValue: item.status },
      ],
      snapshotSummary: `Created ${item.type} "${item.title}"`,
    };

    this.appendEntry(item.id, entry);
    return entry;
  }

  /**
   * Record task mutation and calculate field diffs
   */
  recordMutation(
    oldItem: TaskFlowItem,
    newItem: TaskFlowItem,
    actorId: string
  ): TaskFlowAuditEntry | null {
    const diffs: FieldDiff[] = [];

    // Fields to compare
    const trackedKeys: (keyof TaskFlowItem)[] = [
      'title',
      'description',
      'status',
      'priority',
      'startDate',
      'endDate',
      'dueDate',
      'scheduledDate',
      'completedDate',
      'progressPercentage',
      'parent',
      'tags',
      'assigneeIds',
      'estimateHours',
      'actualHours',
      'relationships',
    ];

    for (const key of trackedKeys) {
      const oldVal = oldItem[key];
      const newVal = newItem[key];

      if (JSON.stringify(oldVal) !== JSON.stringify(newVal)) {
        diffs.push({
          field: String(key),
          oldValue: oldVal ?? null,
          newValue: newVal ?? null,
        });
      }
    }

    if (diffs.length === 0) {
      return null;
    }

    const action =
      newItem.status === 'archived' && oldItem.status !== 'archived'
        ? 'archived'
        : 'updated';

    const entry: TaskFlowAuditEntry = {
      entryId: `audit-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      itemId: newItem.id,
      actorId,
      timestamp: Date.now(),
      action,
      diffs,
      snapshotSummary: `Updated ${diffs.map((d) => d.field).join(', ')}`,
    };

    this.appendEntry(newItem.id, entry);
    return entry;
  }

  /**
   * Append an entry maintaining max entries boundary
   */
  private appendEntry(itemId: string, entry: TaskFlowAuditEntry): void {
    const list = this.history.get(itemId) || [];
    list.push(entry);

    if (list.length > this.maxEntriesPerItem) {
      // Retain the newest entries
      const truncated = list.slice(list.length - this.maxEntriesPerItem);
      this.history.set(itemId, truncated);
    } else {
      this.history.set(itemId, list);
    }
  }

  /**
   * Retrieve audit history for an item
   */
  getHistory(itemId: string): TaskFlowAuditEntry[] {
    return this.history.get(itemId) ? [...this.history.get(itemId)!] : [];
  }

  /**
   * Clear or hydrate history
   */
  hydrate(entries: TaskFlowAuditEntry[]): void {
    this.history.clear();
    for (const entry of entries) {
      this.appendEntry(entry.itemId, entry);
    }
  }

  getAllEntries(): TaskFlowAuditEntry[] {
    const all: TaskFlowAuditEntry[] = [];
    for (const list of this.history.values()) {
      all.push(...list);
    }
    return all;
  }
}
